import * as React from 'react'

import { ChevronDownIcon } from 'lucide-react'
import { action, makeObservable, observable, runInAction } from 'mobx'
import { observer } from 'mobx-react'

import WorkflowTypeIcon from '~/client/src/shared/components/WorkflowTypeIcon/WorkflowTypeIcon'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import ILocationTransferPair from '~/client/src/shared/models/ILocationTransferPair'
import LocationBase from '~/client/src/shared/models/LocationObjects/LocationBase'
import Workflow from '~/client/src/shared/models/Workflow'
import { NOOP } from '~/client/src/shared/utils/noop'
import { areObjectsEqual } from '~/client/src/shared/utils/util'

import { ILocationCellData } from '../MaterialsList.store'
import BaseDataCell from './BaseDataCell/BaseDataCell'
import CurrentLocationCellTable from './CurrentLocationCellTable'
import CurrentLocationStatusIcon from './CurrentLocationStatusIcon'

// localization: translated

interface IProps {
  locationCellData: ILocationCellData
  isTransferEnabled: boolean
  transferFormIconName: string

  onNewTransferClick(
    viewModelId: string,
    selectedLocationsMap: Map<string, ILocationTransferPair>,
  ): void
  onTransferIdClick(workflow: Workflow): void
  locationRenderer(location: LocationBase): JSX.Element
}

const FALSE = () => false

@observer
export default class CurrentLocationCell extends React.Component<IProps> {
  @observable private localProps: IProps = null
  public static defaultProps = {
    locationCellData: {},
  }

  @observable private readonly selectedLocationsMap = new Map<
    string,
    ILocationTransferPair
  >()

  public constructor(props: IProps) {
    super(props)

    this.localProps = props
    makeObservable(this)
  }

  public componentDidUpdate(): void {
    runInAction(() => {
      if (
        !areObjectsEqual(
          this.localProps.locationCellData,
          this.props.locationCellData,
        )
      ) {
        this.localProps.locationCellData = this.props.locationCellData
      }
      if (this.localProps.isTransferEnabled !== this.props.isTransferEnabled) {
        this.localProps.isTransferEnabled = this.props.isTransferEnabled
      }
    })
  }

  public render() {
    const { isTransferEnabled, locationCellData } = this.props

    return (
      <BaseDataCell<ILocationTransferPair>
        items={this.transferPairs}
        newItemIcon={this.iconElement}
        newItemText={Localization.translator.newTransfer}
        onNewItemClick={this.onNewTransferClick}
        renderActiveItems={this.renderLocationsTable}
        renderDoneItems={NOOP}
        doneFilter={FALSE}
        dateRangeFilter={NOOP}
        isNewItemBtnHidden={!isTransferEnabled}
        isNewItemBtnDisabled={!this.selectedLocationsMap.size}
        onModalClose={this.clearSelection}
      >
        <div className="row current-location-cell-value pointer h48 no-select underline-hover">
          <div className="text-ellipsis">{this.cellLabel}</div>
          <CurrentLocationStatusIcon
            iconElement={this.iconElement}
            transferPairs={locationCellData.locationTransferPairs}
            plannedDeliveryLocationId={
              locationCellData.plannedDeliveryLocationId
            }
            plannedInstallLocationId={locationCellData.plannedInstallLocationId}
          />
          <ChevronDownIcon size={12} className="ml5" />
        </div>
      </BaseDataCell>
    )
  }

  private get cellLabel(): string | JSX.Element {
    const { locationRenderer, locationCellData } = this.props
    const { locationTransferPairs } = locationCellData

    if (locationTransferPairs?.length > 1) {
      return Localization.translator.xLocations(locationTransferPairs.length)
    }

    return locationRenderer(locationTransferPairs?.[0]?.location)
  }

  public get iconElement(): JSX.Element {
    return (
      <WorkflowTypeIcon
        iconName={this.localProps.transferFormIconName}
        className="form-id-icon-large relative no-flex mr5"
      />
    )
  }

  private renderLocationsTable = (
    transferPairs: ILocationTransferPair[],
  ): JSX.Element => {
    const {
      locationCellData,
      locationRenderer,
      onTransferIdClick,
      isTransferEnabled,
    } = this.localProps
    return (
      <CurrentLocationCellTable
        isTransferEnabled={isTransferEnabled}
        plannedDeliveryLocationId={locationCellData.plannedDeliveryLocationId}
        plannedInstallLocationId={locationCellData.plannedInstallLocationId}
        transferPairs={transferPairs}
        selectedLocationsMap={this.selectedLocationsMap}
        locationRenderer={locationRenderer}
        onTransferIdClick={onTransferIdClick}
        iconElement={this.iconElement}
      />
    )
  }

  private get transferPairs(): ILocationTransferPair[] {
    return this.props.locationCellData?.locationTransferPairs?.map(tp => ({
      ...tp,
      isSelected: this.selectedLocationsMap.has(tp.location.id),
    }))
  }

  private onNewTransferClick = () => {
    if (this.selectedLocationsMap.size) {
      const { onNewTransferClick, locationCellData } = this.props
      onNewTransferClick(locationCellData.id, this.selectedLocationsMap)
    }
  }

  @action.bound
  private clearSelection() {
    this.selectedLocationsMap.clear()
  }
}
